import { useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { projectSummaries } from "@/projects/projectIndex";
import { trackProjectOpen } from "@/lib/analytics";

export default function ProjectListPage() {
  const navigate = useNavigate();

  const openProject = (project: (typeof projectSummaries)[number]) => {
    void trackProjectOpen(project.id, project.title);
    navigate(`/project/${project.id}`);
  };

  return (
    <div className="min-h-screen bg-stem-cream">
      <Navbar />
      <main>
        {/* Header */}
        <section className="pt-28 md:pt-36 pb-10 md:pb-16 bg-stem-dark">
          <div className="section-padding max-w-6xl mx-auto">
            <button
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm font-medium mb-4 md:mb-6 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line" />
              Back to Home
            </button>
            <span className="label text-revelBlue mb-3 block">My Work</span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-3">
              Projects & Experiments
            </h1>
            <p className="text-white/60 text-sm md:text-base max-w-xl">
              Every project here was explored with curiosity. Click any project
              to read the story in its own shape.
            </p>
          </div>
        </section>

        {/* Projects Grid */}
        <section className="py-16 md:py-24">
          <div className="section-padding max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {projectSummaries.map((project) => (
                <article
                  key={project.id}
                  className="bg-white rounded-2xl overflow-hidden group cursor-pointer transition-all duration-300 hover:-translate-y-1"
                  role="link"
                  tabIndex={0}
                  onClick={() => openProject(project)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") openProject(project);
                  }}
                >
                  {/* Image */}
                  <div className="aspect-[16/10] overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>

                  {/* Content */}
                  <div className="p-5 md:p-6">
                    {/* Meta */}
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                      <span className="px-3 py-1 bg-revelBlue/10 text-revelBlue text-xs font-semibold rounded-full uppercase tracking-wide leading-snug">
                        {project.category}
                      </span>
                      <span className="text-xs text-stem-lightgray">
                        {project.date}
                      </span>
                    </div>

                    {/* Title */}
                    <h2 className="text-lg md:text-xl font-bold text-stem-dark mb-2 leading-snug group-hover:text-revelBlue transition-colors">
                      {project.title}
                    </h2>

                    {/* Description */}
                    <p className="text-sm text-stem-gray leading-relaxed mb-4 line-clamp-2">
                      {project.description}
                    </p>

                    {/* Action */}
                    <div className="flex items-center justify-between">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openProject(project);
                        }}
                        className="btn-dark text-xs py-2 px-4 whitespace-nowrap"
                      >
                        View Project
                        <i className="ri-arrow-right-line text-sm" />
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
